import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Check, CheckCircle2, Copy, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api-client';
import { formatServerDateShort } from '@/lib/dates';
import { useAdminAuthGuard, useAdminInvite, useViewOnlyGate } from '@/features/admin/hooks';
import type { InviteResult } from '@/features/admin/api';
import { AdminShell } from '@/features/admin/components/AdminShell';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { AdminLogoutButton } from '@/features/admin/components/AdminLogoutButton';
import { PendingInvitesList } from '@/features/admin/PendingInvitesList';
import { BillingHealthPanel } from '@/features/admin/BillingHealthPanel';
import { Button } from '@/components/ui/button';
import {
  AuthAlert,
  AuthField,
  AuthInput,
  FieldError,
  type AuthAlertState,
} from './login';

/**
 * Admin dashboard route — the Users page: generate a time-bound invite and
 * surface the resulting signup link. The Terms manager now lives on its own
 * page (/admin/terms); this page is invite-only.
 *
 * Header + card chrome mirror the Terms manager so the two admin pages read as
 * one cohesive console: serif navy title, white card on the line/shadow-card
 * shell, brand-gradient primary action.
 *
 * Bounces to /admin/login if the admin cookie isn't set (via useAdminAuthGuard).
 */

const inviteSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email.'),
  // AI data-use track for this company — the boss decides who-gets-who. Default
  // non_training; drives which data-use agreement they accept at onboarding.
  ai_training_track: z.enum(['non_training', 'training']),
});
type InviteForm = z.infer<typeof inviteSchema>;

export const Route = createFileRoute('/admin/dashboard')({
  component: AdminDashboardPage,
});

export function AdminDashboardPage() {
  useAdminAuthGuard();
  const invite = useAdminInvite();
  const { gate } = useViewOnlyGate();
  const [alert, setAlert] = useState<AuthAlertState>(null);
  const [lastInvite, setLastInvite] = useState<InviteResult | null>(null);

  const form = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', ai_training_track: 'non_training' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: InviteForm) => {
    setAlert(null);
    setLastInvite(null);
    try {
      const res = await invite.mutateAsync(values);
      setLastInvite(res);
      setAlert({ kind: 'success', text: `Invite sent to ${res.email}.` });
      form.reset({ email: '', ai_training_track: 'non_training' });
    } catch (err) {
      setAlert({
        kind: 'error',
        text:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Could not generate the invite.',
      });
    }
  };

  return (
    <AdminShell actions={<AdminLogoutButton />}>
      <AdminPageHeader
        title="Invite a user"
        subtitle="Generate a time-bound invite link and send it to a new teammate."
      />
      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* ── Invite card ── */}
        <div className="bg-white border border-line rounded-xl shadow-card p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-5">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#f4f6f9] text-brand-blue shrink-0">
              <Mail className="size-4" aria-hidden="true" />
            </span>
            <span className="text-navy text-[13px] font-semibold uppercase tracking-[0.08em]">
              New invite
            </span>
          </div>

          <AuthAlert alert={alert} />

          <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <AuthField label="Email Address" htmlFor="invite-email" required>
              <AuthInput
                id="invite-email"
                type="email"
                autoComplete="off"
                placeholder="person@company.com"
                {...form.register('email')}
              />
              <p className="mt-1.5 text-[13px] text-muted m-0">
                An access code is generated automatically. The link is valid for
                7 days and the code can be used only once.
              </p>
              <FieldError message={form.formState.errors.email?.message} />
            </AuthField>

            <AuthField label="AI data-use track" htmlFor="invite-track">
              <div className="flex flex-col gap-2 mt-1" id="invite-track">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    value="non_training"
                    className="mt-1 shrink-0"
                    {...form.register('ai_training_track')}
                  />
                  <span className="text-[13.5px] text-ink leading-snug">
                    <span className="font-medium">Non-training</span>{' '}
                    <span className="text-muted">(default)</span>
                    <span className="block text-[12px] text-muted">
                      Company accepts the Non-Training Agreement — their data is never used to
                      train AI models.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    value="training"
                    className="mt-1 shrink-0"
                    {...form.register('ai_training_track')}
                  />
                  <span className="text-[13.5px] text-ink leading-snug">
                    <span className="font-medium">Training</span>{' '}
                    <span className="text-muted">(opt-in)</span>
                    <span className="block text-[12px] text-muted">
                      Company accepts the Training Agreement — de-identified data may be used to
                      improve the models.
                    </span>
                  </span>
                </label>
              </div>
            </AuthField>

            <Button
              type="submit"
              variant="brand"
              {...gate(invite.isPending)}
              className="w-full h-[44px] rounded-[10px] text-[14px]"
            >
              {invite.isPending ? 'Generating…' : 'Generate invite'}
            </Button>
          </form>

          {lastInvite && <InviteResultPanel invite={lastInvite} />}
        </div>

        {/* ── Billing health ──
            Recorded usage that has not become revenue. Nothing watches the
            backend's ERROR logs, so this panel IS the alerting mechanism. */}
        <BillingHealthPanel />

        {/* ── Pending invites (revoke typo'd / abandoned ones) ── */}
        <PendingInvitesList />
      </div>
    </AdminShell>
  );
}

/**
 * InviteResultPanel — polished success state shown after a successful invite.
 * Subtle success header, the signup link in a mono box with a one-click Copy
 * button (Copy → Check swap, "Copied!" for ~2s), and a formatted expiry line.
 */
function InviteResultPanel({ invite }: { invite: InviteResult }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invite.signup_link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context / denied) — link stays visible
      // for manual copy, so we fail quietly rather than surface an error.
    }
  };

  return (
    <div className="mt-6 rounded-[10px] border border-line bg-slate-50 px-4 py-4 space-y-3">
      <div className="flex items-center gap-2 text-emerald-700">
        <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
        <p className="text-[13px] font-semibold m-0 break-all">
          Invite ready for {invite.email}
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold text-muted uppercase tracking-[0.06em]">
          Signup link
        </label>
        <div className="flex items-stretch gap-2">
          <div className="flex-1 min-w-0 break-all bg-[#f4f6f9] border border-line rounded-[10px] py-3 px-3.5 text-[13px] text-navy font-mono">
            {invite.signup_link}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleCopy()}
            aria-label={copied ? 'Copied' : 'Copy signup link'}
            className={cn(
              'h-auto self-stretch px-3 rounded-[10px] border-line text-navy shrink-0',
              copied && 'text-emerald-700 border-emerald-200 bg-emerald-50',
            )}
          >
            {copied ? (
              <>
                <Check className="size-4" aria-hidden="true" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="size-4" aria-hidden="true" />
                Copy
              </>
            )}
          </Button>
        </div>
      </div>

      <p className="text-[12px] text-muted m-0">
        Expires {formatServerDateShort(invite.expires_at)}.
      </p>
    </div>
  );
}
